import dayjs from "dayjs";
import styled from "styled-components";

import type { ILegendItem } from "@similarweb/pro-logic-components/dist/graphs/components/GenericChartLegend/types";
import { colorsPalettes } from "@similarweb/styles";
import type { ILegendProps } from "@similarweb/ui-components";

import { KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";
import { ALL } from "app/common/services/categoryService";
import ColorStack from "app/components/colorsStack/ColorStack";
import { createUniqueListOfAllDomains, getFilteredDomain } from "pro-features/components/DomainsFilter/Utils";
import Legends from "app/components/React/Legends/Legends";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { ACTIVATION_USERDATA_API } from "app/pages/activation/services/constants";
import type { ProjectsResponseSchema } from "app/pages/activation/types/api";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { EGraphType, webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { BasicDurations, apiFormat, customRangeFormat } from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import { KeywordAnalysisGraphDashboardWidgetFilters } from "../widget-filters/KeywordAnalysisGraphDashboardWidgetFilters";
import { KeywordAnalysisGraphSubtitle } from "../widget-subtitle-templates/KeywordAnalysisGraphSubtitle";
import { isValidUuidForKeywordGroup } from "../widget-utilities/isValidUuidForKeywordGroup";
import { getCategories, getDomains } from "../widget-utilities/KeywordAnalysisUtils";

import { GraphWidget } from "./GraphWidget";
import {
    KeywordAnalysisTableWidgetAPIParams,
    KeywordAnalysisWidgetFiltersMap,
} from "./TableKeywordAnalysisDashboardConst";
import { WidgetFilter } from "./TableKeywordAnalysisDashboardWidget";
import type { ISubtitleTextItem } from "./Widget";

const GraphKeywordAnalysisLegendsWrapper = styled.div`
    margin: 16px 0;
`;
const MAX_WEBSITES_TO_DISPLAY = 5;

export type DataPoint = { Key: string; Value: string };
export interface ITransformedData {
    Data: { [key: string]: { [key: string]: DataPoint[] } };
}

interface IGraphData {
    SitesClicks: { [domain: string]: { [date: string]: number } };
    SitesWithNoClicks: string[];
    Totals: { [date: string]: number };
}
interface ILegendObject {
    id: string;
    name: string;
    image?: string;
}

interface ViewOptions {
    cssClass: string;
    loadingSize: number;
    showTitle: boolean;
    showTitleTooltip: boolean;
    showSubtitle: boolean;
    showLegend: boolean;
    legendAlign: string;
    showFrame: boolean;
    showSettings: boolean;
    showTopLine: boolean;
    titleType: string;
    plotLines: any[];
}

interface ChartOptions {
    xAxis: Array<{
        minPadding: number;
        maxPadding: number;
        plotLines: any[];
    }>;
    yAxis: Array<{
        showFirstLabel: boolean;
        showLastLabel: boolean;
        reversed: boolean;
        gridZIndex: number;
        reversedStacks: boolean;
        tickPositioner: any;
        labels: object;
    }>;
    legend: {
        enabled: boolean;
    };
}

interface Exporting {
    chartOptions: ChartOptions;
}

interface ChartConfig {
    type: string;
    markerEnabled: boolean | null;
    format: string;
    formatParameter: number;
    reversed: boolean;
    height: number;
    stacked: boolean;
    legendAlign: string;
    hideMarkersOnDaily: boolean;
    showStacked: boolean;
    showLegend: boolean;
    hasCompareLegend: boolean;
    timeGranularity: string;
    xAxisTickInterval: number;
    params: any;
    viewOptions: ViewOptions;
    exporting: Exporting;
    chart: any;
    plotOptions: any;
    yAxis: any;
    xAxis: any;
}
type IDataResponse = [string, { [date: string]: number }][];
const fetchService = DefaultFetchService.getInstance();

const getActivationSets = async () =>
    window.similarweb.settings.components.Activation.isDisabled
        ? []
        : (await fetchService.get<ProjectsResponseSchema | null>(ACTIVATION_USERDATA_API))?.activationSets;

function createDataFetcherFactory(widget) {
    return {
        fetch: async (apiParams = widget.apiParams) => {
            const { duration, graphDuration, domains, category, ...restApiParams } = apiParams;
            const activationSets = await getActivationSets();
            const allDomains = createUniqueListOfAllDomains(domains?.split(","), activationSets);

            const _params = {
                ...restApiParams,
                page: 1,
                pageSize: 100,
                Device: webSourceToDeviceMap[apiParams.webSource],
                graphType: EGraphType[EGraphType[apiParams.graphType]],
                Duration: apiParams.graphDuration,
                DomainsFilter: allDomains,
                category: category === ALL ? undefined : category,
            };

            const data = fetchService.get(`${widget._metricConfig.endpoint}`, _params);

            const domainsFromServer = await getDomains(widget._metricConfig.domainsEndpoint, requestParams(_params));
            const domainsList = domainsFromServer && getFilteredDomain(domainsFromServer, activationSets);
            const categories = await getCategories(widget._metricConfig.categoriesEndpoint, requestParams(_params));

            const [Data, Categories, Domains] = await Promise.all([data, categories, domainsList]);
            return { Data, Categories, Domains };
        },
    };
}

export const createLegendObject = (data: ILegendItem[]): ILegendObject[] => {
    const legendObject = data?.map((item) => ({
        id: item.name,
        name: item.name,
        color: item.color,
    }));
    return legendObject as ILegendObject[];
};

export const GraphKeywordAnalysisLegends = (props) => {
    return (
        <GraphKeywordAnalysisLegendsWrapper>
            <Legends {...props} />
        </GraphKeywordAnalysisLegendsWrapper>
    );
};

export class KeywordAnalysisGraphDashboardWidget extends GraphWidget {
    private colorStack;

    reactLegendComponent: (props: ILegendProps) => React.ReactNode;
    constructor() {
        super();
        this.colorStack = new ColorStack(CHART_COLORS.chartMainColors);
        this.reactLegendComponent = GraphKeywordAnalysisLegends;
    }

    public isPptSupported = () => false;

    static getWidgetMetadataType() {
        return "KeywordAnalysisGraphDashboard";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    private dataFetcherFactory = () => {
        return {
            create: createDataFetcherFactory,
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    public async onNewDataReceived(response): Promise<void> {
        const transformedData = this.transformData(response);
        await super.onNewDataReceived(transformedData);
    }

    public callbackOnGetData(response) {
        this.data = {
            ...this.data,
            filters: {
                Categories: response.Categories,
                DomainsList: response.Domains,
            },
        };
        if (Object.keys(response.Data).length !== 0 || Object.keys(response.Data.SitesClicks).length !== 0) {
            return super.callbackOnGetData(
                {
                    Data: response.Data,
                    Categories: response.Categories,
                    Domains: response.Domains,
                },
                Object.keys(response.Data),
            );
        }
    }

    static getFiltersComponent() {
        return KeywordAnalysisGraphDashboardWidgetFilters;
    }

    getLegendItems(data?: ILegendItem[]) {
        return data && createLegendObject(data);
    }

    getHighChartsConfig(chartOptions: ChartConfig, formattedData: ITransformedData, data: IGraphData) {
        const { graphType } = this._params;
        const isStackedShare = Number(graphType) === EGraphType["chart-stacked-share"];
        chartOptions = {
            ...chartOptions,
            ...(isStackedShare
                ? {
                      type: "percentage",
                      format: "percentagesign",
                      formatParameter: 1,
                  }
                : { type: "long", format: "number", formatParameter: 0 }),

            showLegend: false,
            chart: {
                type: Number(graphType) === EGraphType.overview ? chartTypes.LINE : chartTypes.AREA,
                spacingTop: 10,
                marginTop: 27,
                marginBottom: 27,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                line: {
                    marker: {
                        enabled: false,
                    },
                },
                area: {
                    marker: {
                        enabled: false,
                    },
                    stacking: "normal",
                    connectNulls: true,
                    fillOpacity: 1,
                    lineWidth: 0.5,
                },
                series: {
                    fillOpacity: 1,
                    animation: false,
                },
            },

            yAxis: {
                ...(isStackedShare
                    ? {
                          min: 0,
                          max: 1,
                          tickAmount: 5,
                      }
                    : { min: undefined, max: undefined, tickAmount: undefined }),
            },

            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: colorsPalettes.carbon[250],
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
                type: "datetime",
                tickPositioner: function () {
                    if (this.chart.series.length > 30) {
                        return this.chart.series.filter((_, index) => {
                            return index % 10 == 0;
                        });
                    }
                    if (this.chart.series.length > 8) {
                        return this.chart.series.filter((_, index) => {
                            return index % 3 == 0;
                        });
                    }
                    if (this.chart.series.length > 6) {
                        return this.chart.series.filter((_, index) => {
                            return index % 2 == 0;
                        });
                    }
                    return this.chart.series;
                },
            },
        };

        return data && super.getHighChartsConfig(chartOptions, formattedData, data);
    }

    getViewData() {
        super.getViewData();
        const { webSource } = this._widgetConfig.properties;
        const { keys, domains } = this._params;
        if (!this._widgetConfig.properties.country && !webSource) {
            return this.viewData;
        }
        this.viewData.customSubtitle = "true";
        this.viewData.subtitleTextItem = this.getFiltersTooltip();
        this.viewData.customSubtitleComponent = KeywordAnalysisGraphSubtitle;
        return this.viewData;
    }
    getFiltersTooltip(): ISubtitleTextItem {
        const mappedFilters = KeywordAnalysisTableWidgetAPIParams.map((filter) =>
            KeywordAnalysisWidgetFiltersMap[filter](this._params),
        );
        const availableFilters = mappedFilters.filter((filter) => filter !== null);
        const amountOfFilters = availableFilters.length;

        if (!amountOfFilters) {
            return;
        }

        const filtersTextSubtitle = (() => {
            switch (amountOfFilters) {
                case 0:
                    return availableFilters[0];
                case 1:
                    return translate("dashboard.widget.subtitle.landing.page.filters.unique");
                default:
                    return translate("dashboard.widget.subtitle.landing.page.filters.multiple", {
                        amount: amountOfFilters,
                    });
            }
        })();

        const tooltipValue = availableFilters.map((filter, index) => <WidgetFilter key={index}>{filter}</WidgetFilter>);
        return {
            text: filtersTextSubtitle,
            tooltipValue,
        };
    }

    public validateData(response: IGraphData) {
        return Object.keys(response).length !== 0;
    }

    public getProUrl() {
        const {
            country,
            webSource,
            to,
            latest,
            keys,
            source,
            sourceType,
            graphDuration,
            graphType,
            timeGranularity,
            category,
            domains,
            selectedPageTab,
            funcFlag,
        } = this.apiParams;

        const isKeywordGroup = isValidUuidForKeywordGroup(keys);

        const sourceKey = Object.keys(SourceTypeMap).find(
            (key) => SourceTypeMap[key as keyof typeof EKeywordTabs] === sourceType,
        );
        const formattedKeys = isKeywordGroup ? `${KEYWORD_GROUP_PREFIX}${keys}` : keys;
        const is28Days = latest === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
        const selectedMonth = dayjs.utc(to, apiFormat).format(customRangeFormat);
        const toParams = {
            country,
            duration: is28Days ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : `${selectedMonth}-${selectedMonth}`,
            webSource,
            key: formattedKeys,
            keyword: formattedKeys,
            source,
            selectedPageTab: sourceKey,
            graphDuration: graphDuration,
            graphType,
            timeGranularity,
            sourceType,
            category,
            domains,
            funcFlag,
            ...(is28Days ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        };
        return this._swNavigator.getStateUrl(this._metricConfig.state, {
            ...toParams,
        });
    }

    private sortByAverage(data: IDataResponse): IDataResponse {
        const averages = data.map(([domain, values]) => {
            const total = Object.values(values).reduce((acc: number, curr: number) => acc + curr, 0);
            const average = total / Object.keys(values).length;
            return [domain, average];
        });

        averages.sort((a, b) => (b[1] as number) - (a[1] as number));

        const sortedData = averages.map(([domain]) => {
            return data.find((entry) => entry[0] === domain);
        });

        return sortedData;
    }

    private transformData(response) {
        if (!response && !response.Data) {
            return;
        }
        const dataArray: IDataResponse = Object.entries(response.Data.SitesClicks);
        const sorted = this.sortByAverage(dataArray).slice(0, MAX_WEBSITES_TO_DISPLAY);
        const transformedData = {};

        const { graphType } = this._params;
        const isStackedShare = Number(graphType) === EGraphType["chart-stacked-share"];

        sorted.forEach(([domain, values]) => {
            const nestedData = Object.entries(values).reduce((acc, [key, value]) => {
                const total = response.Data.Totals[key];
                const shareValue = total === 0 ? 0 : value / total;
                const newEntry = { Key: key, Value: isStackedShare ? shareValue : value };
                return [...acc, newEntry];
            }, []);

            transformedData[domain] = {
                [domain]: nestedData,
            };
        });
        return { Data: transformedData, Categories: response.Categories, Domains: response.Domains };
    }
}

KeywordAnalysisGraphDashboardWidget.register();
